/**
 * Small text helpers of the Notification Center rows and toasts (#1093):
 * the short "when", the meta line, the label of the inline action and the
 * plain-text preview of title and message.
 */
import type { TFunction } from 'i18next'
import type { Notification } from '@/services/notification-service'
import { notificationCategory } from '@/lib/utils/notificationPayload'
import { notificationText, type NotificationText } from '@/lib/utils/notificationText'

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

/** "2 Min." / "3 Std." within a day, a weekday within the last week, a date before. */
export function formatWhen(createdAt: string, t: TFunction, language: string, now: Date = new Date()): string {
  const created = new Date(createdAt)
  if (Number.isNaN(created.getTime())) return ''
  const minutes = Math.floor((now.getTime() - created.getTime()) / 60000)
  if (minutes < 1) return t('notificationCenter.when.justNow')
  if (minutes < 60) return t('notificationCenter.when.minutes', { count: minutes })
  if (minutes < 24 * 60) return t('notificationCenter.when.hours', { count: Math.floor(minutes / 60) })
  if (minutes < 7 * 24 * 60) return created.toLocaleDateString(language, { weekday: 'short' })
  return created.toLocaleDateString(language, { day: '2-digit', month: '2-digit' })
}

/** Full date and time for the tooltip of the short "when". */
export function formatExact(createdAt: string, language: string): string {
  const created = new Date(createdAt)
  if (Number.isNaN(created.getTime())) return ''
  return created.toLocaleString(language, { dateStyle: 'medium', timeStyle: 'short' })
}

/** What the row is about, besides its category: a report number or a knowledge area. */
function objectLabel(notification: Notification): string | undefined {
  const data = notification.data ?? {}
  return str(data.report_number) ?? str(data.knowledge_area) ?? str(data.session_title) ?? str(data.prompt_title)
}

/** "Bug-Report · BR-0142 · Muster GmbH" — category, object, client; missing parts are left out. */
export function notificationMeta(notification: Notification, t: TFunction): string {
  const category = notificationCategory(notification)
  const parts = [
    category ? t(`notificationCenter.meta.${category}`) : undefined,
    objectLabel(notification),
    str(notification.data?.client_name),
  ].filter((part): part is string => !!part)
  return [...new Set(parts)].join(' · ')
}

/** Label of the inline button of an `action_required` row. */
export function primaryActionLabel(notification: Notification, t: TFunction): string {
  switch (notification.type) {
    case 'quota_request':
    case 'access_request':
      return t('notificationCenter.actions.review')
    case 'bug_report_reply':
    case 'bug_report_status_changed':
      return t('notificationCenter.actions.reply')
    default:
      return t('notificationCenter.actions.open')
  }
}

/**
 * Markdown syntax out of a one- or two-line preview. Routine results and
 * other server texts arrive as Markdown ("**AI-Nachrichten** …"); a row shows
 * them as plain text, never rendered — the text is only ever a React text
 * node. Keeps the words, drops the markup; collapses line breaks to spaces.
 */
export function stripMarkdown(text: string): string {
  return (
    text
      // Fenced code: keep the code, drop the fences and the language tag.
      .replace(/```[^\n]*\n?/g, '')
      // Images and links: the visible text only.
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
      // Horizontal rules, before the bullets would eat "* * *".
      .replace(/^[ \t]*([-*_])([ \t]*\1){2,}[ \t]*$/gm, '')
      // Line-leading markers: headings, quotes, list bullets, numbered items.
      .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
      .replace(/^[ \t]*>[ \t]?/gm, '')
      .replace(/^[ \t]*[-*+][ \t]+/gm, '')
      .replace(/^[ \t]*\d+[.)][ \t]+/gm, '')
      // Emphasis, strike-through, inline code. `_` only at word edges, so
      // snake_case names stay intact.
      .replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, '$2')
      .replace(/~~(?=\S)([\s\S]*?\S)~~/g, '$1')
      .replace(/\*(?=\S)([^*\n]*?\S)\*/g, '$1')
      .replace(/(^|[^\w])_(?=\S)([^_\n]*?\S)_(?!\w)/g, '$1$2')
      .replace(/`([^`\n]+)`/g, '$1')
      .replace(/\s+/g, ' ')
      .trim()
  )
}

/** Title and message of a row or toast as plain text (see `stripMarkdown`). */
export function notificationPreview(t: TFunction, notification: Notification, language?: string): NotificationText {
  const text = notificationText(t, notification, language)
  return {
    ...text,
    title: stripMarkdown(text.title),
    message: text.message ? stripMarkdown(text.message) : text.message,
  }
}
